import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingDown, Award, Leaf, Zap, Flag, Wrench, Star, Thermometer, Wind, Volume2, Gauge, RefreshCw, Calculator, ArrowRight, Flame, Fuel, Trophy, Wheat, Anchor, Factory } from 'lucide-react';
import HeroCarousel from '../components/HeroCarousel';
import ProductCardNew from '../components/ProductCardNew';
import AvisClients from '../components/AvisClients';
import { products, benefits, technicalFeatures } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent } from '../components/ui/card';

const iconMap = {
  TrendingDown, Award, Leaf, Zap, Flag, Wrench,
  Thermometer, Wind, Volume2, Gauge, RefreshCw
};

const HomeNew = () => {
  const { toast } = useToast();

  const handleAddToCart = (product, withInstallation) => {
    const price = withInstallation ? product.priceWithInstall : product.priceWithoutInstall;
    toast({
      title: "Produit ajouté !",
      description: `${product.name} (${price} CHF) ajouté à votre panier.`,
      duration: 3000,
    });
  };

  return (
    <div className="min-h-screen">
      <HeroCarousel />

      {/* Stats Banner */}
      <section className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">15-35%</div>
              <div className="text-sm opacity-90">Économie de carburant</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">-30%</div>
              <div className="text-sm opacity-90">Émissions de CO₂</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">-80%</div>
              <div className="text-sm opacity-90">Opacité gaz échappement</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">-40 à 60%</div>
              <div className="text-sm opacity-90">Baisse des NOx</div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent">
                Kit Universel Révolution'Air
              </span>
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              Un système unique, écologique, compatible avec tous les moteurs pour une économie 
              de carburant de <strong className="text-[#7CB342]">15 à 35%</strong>. 
              À chaque litre de carburant économisé, c'est l'air de 5 personnes sauvegardées de la pollution.
            </p>
            
            {/* Drapeaux Suisse-France */}
            <div className="flex flex-col md:flex-row justify-center items-center gap-6 mb-8">
              <div className="bg-gradient-to-br from-red-50 to-white border-2 border-red-200 rounded-xl px-8 py-4 shadow-lg">
                <div className="flex items-center gap-3">
                  <span className="text-5xl">🇨🇭</span>
                  <div className="text-left">
                    <div className="font-bold text-xl text-gray-900">Suisse</div>
                    <div className="text-sm text-gray-600">Distribution & Installation</div>
                    <div className="text-xs text-gray-500 mt-1">Steve Rosset - Sion, Valais</div>
                  </div>
                </div>
              </div>
              
              <div className="hidden md:block">
                <svg width="40" height="40" viewBox="0 0 40 40" className="text-[#00BCD4]">
                  <path d="M15 20 L25 20 M20 15 L25 20 L20 25" stroke="currentColor" strokeWidth="2" fill="none"/>
                </svg>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-xl px-8 py-4 shadow-lg">
                <div className="flex items-center gap-3">
                  <span className="text-5xl">🇫🇷</span>
                  <div className="text-left">
                    <div className="font-bold text-xl text-gray-900">France</div>
                    <div className="text-sm text-gray-600">Fabrication & Innovation</div>
                    <div className="text-xs text-gray-500 mt-1">Made in Auvergne • Depuis 2007</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4">
              <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg px-6 py-3 flex items-center gap-2">
                <Award className="text-yellow-600" size={24} />
                <span className="font-semibold text-gray-900">Edison Awards 2023 Nominee</span>
              </div>
              <div className="bg-blue-50 border-2 border-blue-400 rounded-lg px-6 py-3 flex items-center gap-2">
                <Flag className="text-blue-600" size={24} />
                <span className="font-semibold text-gray-900">SAS ECOPRA - Inventeur</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            <span className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent">
              Pourquoi choisir Ecopra ?
            </span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit) => {
              const IconComponent = iconMap[benefit.icon];
              const colorMap = {
                cyan: 'from-[#00BCD4] to-[#0097A7]',
                green: 'from-[#7CB342] to-[#689F38]',
                yellow: 'from-yellow-400 to-yellow-500',
                blue: 'from-blue-500 to-blue-600'
              };
              return (
                <Card key={benefit.id} className="text-center hover:shadow-xl transition-all transform hover:-translate-y-2 border-2 border-transparent hover:border-[#00BCD4]">
                  <CardContent className="pt-8 pb-6">
                    <div className={`inline-block p-4 bg-gradient-to-br ${colorMap[benefit.color] || 'from-[#00BCD4] to-[#0097A7]'} rounded-2xl mb-4 shadow-lg`}>
                      <IconComponent className="text-white" size={36} />
                    </div>
                    <h3 className="font-bold text-xl text-gray-900 mb-3">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technical Features */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            <span className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent">
              Caractéristiques techniques
            </span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {technicalFeatures.map((feature, index) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <div key={index} className="bg-gradient-to-br from-[#7CB342]/5 to-[#00BCD4]/5 rounded-lg p-6 border-2 border-[#00BCD4]/20 hover:border-[#00BCD4] transition-all hover:shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-white rounded-lg shadow-md">
                      <IconComponent className="text-[#00BCD4]" size={24} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">{feature.title}</h4>
                      <p className="text-sm text-gray-600">{feature.subtitle}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent">
                Nos kits pour la Suisse
              </span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Choisissez le kit adapté à votre véhicule. Installation professionnelle disponible partout en Suisse.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <ProductCardNew
                key={product.id}
                product={product}
                onAddToCart={handleAddToCart}
              />
            ))}
          </div>
        </div>
      </section>

      {/* === COMPATIBILITÉ ÉTENDUE — Applications spéciales === */}
      <section
        data-testid="home-applications-section"
        className="py-16 md:py-20 bg-gradient-to-b from-white to-gray-50"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-[#7CB342]/10 text-[#7CB342] px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
              <Flame size={14} />
              <span>Au-delà des véhicules</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Une solution pour <span className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent">chaque besoin</span>
            </h2>
            <p className="text-base md:text-lg text-gray-600">
              Le Kit BuVeurdAir s'adapte à de nombreuses applications, bien au-delà des véhicules classiques.
              Chauffage, agriculture, compétition, marine ou industrie — nous avons une solution sur mesure.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 max-w-6xl mx-auto">
            {[
              { icon: Flame,        title: 'Poêle à bois',         desc: 'Combustion optimisée' },
              { icon: Flame,        title: 'Poêle de masse',       desc: 'Rendement amélioré' },
              { icon: Flame,        title: 'Poêle à pellet',       desc: 'Économie granulés' },
              { icon: Fuel,         title: 'Chaudière à fioul',    desc: 'Consommation réduite' },
              { icon: Thermometer,  title: 'Webasto / auxiliaire', desc: 'Chauffage véhicule' },
              { icon: Trophy,       title: 'Voitures de collection', desc: 'Préservation moteur' },
              { icon: Zap,          title: 'Compétition',          desc: 'Sur mesure performance' },
              { icon: Wheat,        title: 'Viticulture / agricole', desc: 'Tracteurs, vendangeuses' },
              { icon: Anchor,       title: 'Bateaux & marine',     desc: 'Moteurs nautiques' },
              { icon: Factory,      title: 'Industriel sur mesure', desc: 'Incinérateurs, groupes' },
            ].map((app, idx) => {
              const Ico = app.icon;
              return (
                <div
                  key={idx}
                  data-testid={`home-application-${idx}`}
                  className="group bg-white border border-gray-200 hover:border-[#00BCD4] rounded-xl p-5 text-center transition-all hover:shadow-lg hover:-translate-y-1 duration-200"
                >
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-[#7CB342]/10 to-[#00BCD4]/10 group-hover:from-[#7CB342] group-hover:to-[#00BCD4] flex items-center justify-center transition-all">
                    <Ico
                      size={22}
                      className="text-[#00BCD4] group-hover:text-white transition-colors"
                    />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm mb-1 leading-tight">
                    {app.title}
                  </h3>
                  <p className="text-xs text-gray-500 leading-tight">{app.desc}</p>
                </div>
              );
            })}
          </div>

          {/* CTA bandeau "Sur mesure" */}
          <div className="mt-10 max-w-3xl mx-auto">
            <div className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] rounded-2xl p-6 md:p-8 text-white text-center shadow-lg">
              <h3 className="text-xl md:text-2xl font-bold mb-2">
                Votre application n'est pas listée ?
              </h3>
              <p className="text-white/90 mb-5 text-sm md:text-base">
                Nous concevons des réacteurs sur mesure (BVA 5 AD à BVA 8 BV) pour les applications spéciales :
                viticulture, compétition, voiture de collection, incinérateur, gros industriel...
              </p>
              <Link
                to="/contact?subject=Demande%20de%20kit%20sur%20mesure&message=Bonjour%2C%0A%0AJe%20souhaite%20un%20devis%20pour%20une%20application%20particuli%C3%A8re%20%3A%0A%0AType%20d'application%20%3A%0APuissance%20%2F%20cylindr%C3%A9e%20%3A%0AUtilisation%20%3A%0A%0AMerci."
                data-testid="home-applications-cta-btn"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-[#00BCD4] font-bold px-7 py-3.5 rounded-xl text-sm md:text-base shadow-md transition-all hover:scale-[1.02]"
              >
                Demander un devis sur mesure
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* === END COMPATIBILITÉ ÉTENDUE === */}

      {/* === SIMULATEUR CTA — High-conversion banner === */}
      <section
        data-testid="home-simulateur-cta"
        className="relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0a3d1a 0%, #1a5c2e 55%, #0f4a22 100%)',
        }}
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 70% 60% at 50% 100%, rgba(46,189,94,.18), transparent 70%)',
          }}
        />
        <div className="container mx-auto px-4 py-14 md:py-20 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center max-w-6xl mx-auto">
            {/* LEFT — copy */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur rounded-full px-4 py-1.5 text-xs font-medium text-white/90 mb-5">
                <Calculator size={14} className="text-[#7ef5a0]" />
                <span>Simulation gratuite · sans engagement · 30 secondes</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
                Combien pouvez-vous{' '}
                <span style={{ color: '#7ef5a0' }}>économiser</span>{' '}
                avec le BVA&nbsp;?
              </h2>
              <p className="text-base md:text-lg text-white/70 mb-8 max-w-xl mx-auto lg:mx-0">
                Renseignez votre véhicule et votre consommation, découvrez instantanément :
                vos économies par an, le coût du kit + installation, et en combien de mois il est remboursé.
              </p>

              <div className="flex flex-wrap gap-2 justify-center lg:justify-start mb-8 text-sm">
                <span className="bg-white/10 border border-white/15 text-white/85 px-3 py-1 rounded-full">💰 Économies annuelles</span>
                <span className="bg-white/10 border border-white/15 text-white/85 px-3 py-1 rounded-full">⏱ ROI en mois</span>
                <span className="bg-white/10 border border-white/15 text-white/85 px-3 py-1 rounded-full">📅 Gain net 5 ans</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Link
                  to="/simulateur"
                  data-testid="home-simulateur-cta-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#7ef5a0] hover:bg-[#6ae08c] text-[#0a3d1a] font-bold px-7 py-4 rounded-xl text-base shadow-lg transition-all hover:scale-[1.02]"
                >
                  <Calculator size={18} />
                  Lancer ma simulation
                  <ArrowRight size={18} />
                </Link>
                <Link
                  to="/boutique"
                  className="inline-flex items-center justify-center gap-2 bg-transparent hover:bg-white/10 text-white border border-white/30 font-semibold px-6 py-4 rounded-xl text-base transition-all"
                >
                  Voir la boutique
                </Link>
              </div>
            </div>

            {/* RIGHT — preview card */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-md mx-auto lg:ml-auto">
                <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">
                  Exemple · Voiture 15&nbsp;000 km/an
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span
                    className="text-5xl md:text-6xl font-bold leading-none"
                    style={{ fontFamily: "'DM Mono', monospace", color: '#1a7a3c', letterSpacing: '-2px' }}
                  >
                    449
                  </span>
                  <span className="text-2xl font-semibold text-gray-700">CHF</span>
                </div>
                <div className="text-sm text-gray-600 mb-5">économisés par an avec 20 % de réduction</div>

                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-100">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Kit + pose</div>
                    <div className="text-lg font-bold text-gray-900" style={{ fontFamily: "'DM Mono', monospace" }}>
                      499 CHF
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Remboursé en</div>
                    <div className="text-lg font-bold" style={{ fontFamily: "'DM Mono', monospace", color: '#1a7a3c' }}>
                      ~13 mois
                    </div>
                  </div>
                </div>

                <div className="mt-5 inline-block bg-[#1a7a3c]/10 border border-[#1a7a3c]/20 text-[#1a7a3c] text-xs font-semibold px-3 py-1.5 rounded-full">
                  ✅ Remboursé en moins de 14 mois
                </div>
              </div>
              {/* Decorative glow */}
              <div
                className="absolute -top-6 -right-6 w-32 h-32 rounded-full opacity-30 blur-3xl pointer-events-none hidden lg:block"
                style={{ background: '#7ef5a0' }}
              />
            </div>
          </div>
        </div>
      </section>
      {/* === END SIMULATEUR CTA === */}

      {/* Avis Clients - New Component */}
      <AvisClients />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Prêt à économiser du carburant ?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-95">
            Contactez-nous pour une installation professionnelle partout en Suisse.
            Devis gratuit et sans engagement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-block bg-white text-[#00BCD4] font-bold px-10 py-4 rounded-full hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl"
            >
              Demander un devis gratuit
            </Link>
            <Link
              to="/boutique"
              className="inline-block bg-transparent border-2 border-white text-white font-bold px-10 py-4 rounded-full hover:bg-white/10 transition-all transform hover:scale-105"
            >
              Voir tous les kits
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomeNew;
